import { FC, useMemo, useCallback, memo } from 'react'
import { ActionMenu } from 'components/features/pfm/ActionMenu/ActionMenu'
import { AnalyticsClick } from 'components/features/pfm/AnalyticsClick/AnalyticsClick'
import { ActionMenuItem } from 'components/features/pfm/ActionMenu/ActionMenuItem'
import SvgIcon from 'components/atoms/SvgIcon'
import EditSvg from 'icons/pencil.svg'
import CrossSvg from 'icons/cross.svg'
import FollowAuthorSvg from 'icons/follow-author.svg'
import UnfollowAuthorSvg from 'icons/unfollow-author.svg'
import ReportSvg from 'icons/report.svg'
import {
  PostUidOwnProps,
  createIsPostEditingSelector,
  createFeedUserSelector,
  createIsFeedUserMemberSelector,
  createAuthorFollowingLoadingSelector,
  createRegularPostSelector,
} from 'selectors/feedSelectors'
import { useStoreSelector, useStoreDispatch, useShallowSelector } from 'helpers/reactReduxHelpers'
import { FeedLinkLoggedOut } from 'components/features/socialFeed/FeedLinkLoggedOut/FeedLinkLoggedOut'
import {
  deletePostActionCreator,
  reportPostActionCreator,
  toggleAuthorFollowingActionCreator,
  reportInsightActionCreator,
  toggleEditPostActionCreator,
} from 'actions/feedActions'
import styles from 'components/features/pfm/PostActionsMenu/PostActionsMenu.styl'

type FeedPostActionsMenuProps =
  & PostUidOwnProps
  & {
    onClose?: () => void
  }

export const FeedPostActionsMenu: FC<FeedPostActionsMenuProps> = memo((props) => {
  const {
    postUid,
    onClose,
  } = props
  const isPostEditingSelector = useMemo(createIsPostEditingSelector, [])
  const isPostEditing = useStoreSelector((state) => isPostEditingSelector(state, props))

  const feedPostSelector = useMemo(createRegularPostSelector, [])
  const post = useStoreSelector((state) => feedPostSelector(state, props))

  const withUserUid = useMemo(() => ({ userUid: post && post.authorUid || '' }), [post])

  const feedUserSelector = useMemo(createFeedUserSelector, [])
  const {
    is_followed,
    username,
  } = useShallowSelector((state) => {
    return {
      is_followed: null,
      username: null,
      ...feedUserSelector(state, withUserUid),
    }
  })

  const isFeedUserMemberSelector = useMemo(createIsFeedUserMemberSelector, [])
  const isOwn = useStoreSelector((state) => isFeedUserMemberSelector(state, withUserUid))

  const dispatch = useStoreDispatch()

  const handleEditPost = useCallback(() => {
    dispatch(toggleEditPostActionCreator({ isEditing: true, postUid }))
    onClose && onClose()
  }, [postUid, dispatch, onClose])

  const handleDeletePost = useCallback(() => {
    dispatch(deletePostActionCreator.request({ postUid }))
    onClose && onClose()
  }, [postUid, dispatch, onClose])

  const handleSendReport = useCallback(() => {
    if (post && post.is_reported) {
      return
    }

    if (post && post.type === 'sponsored_post') {
      dispatch(reportInsightActionCreator.request({ postUid, insightId: post.insight.id }))
    } else {
      dispatch(reportPostActionCreator.request({ postUid }))
    }
    onClose && onClose()
  }, [post, onClose, dispatch, postUid])

  const authorFollowingLoadingSelector = useMemo(createAuthorFollowingLoadingSelector, [])
  const isAuthorFollowingLoading = useStoreSelector((state) => authorFollowingLoadingSelector(state, withUserUid))
  const handleFollowAuthorClick = useCallback(() => {
    if (is_followed === null || isAuthorFollowingLoading) {
      return
    }
    dispatch(toggleAuthorFollowingActionCreator.request({ toFollow: !is_followed, uid: withUserUid.userUid }))
    onClose && onClose()
  }, [withUserUid.userUid, dispatch, onClose, is_followed, isAuthorFollowingLoading])

  const followActionButton = useMemo(() => {
    if (is_followed === null || username === null) {
      return null
    }

    const followActionTitle = `${ is_followed ? 'Unfollow' : 'Follow' }`

    return (
      <AnalyticsClick
        category='module-feed_post'
        action='toggle-follow_author'
        label={ is_followed ? 'unfollow' : 'follow' }
      >
        <FeedLinkLoggedOut type='post' postUid={ postUid }>
          {
            (isAuthenticated) => {
              const icon = (
                <SvgIcon src={ is_followed ? UnfollowAuthorSvg : FollowAuthorSvg } />
              )
              const handleClick = isAuthenticated ? handleFollowAuthorClick : undefined

              return (
                <ActionMenuItem
                  icon={ icon }
                  hint={ `${ followActionTitle } ${ username }` }
                  isDisabled={ isAuthorFollowingLoading }
                  onClick={ handleClick }
                >
                  { followActionTitle }
                  <span style={{ textTransform: 'none' }}> { username }</span>
                </ActionMenuItem>
              )
            }
          }
        </FeedLinkLoggedOut>
      </AnalyticsClick>
    )
  }, [is_followed, username, postUid, handleFollowAuthorClick, isAuthorFollowingLoading])

  const reportActionButton = useMemo(() => {
    if (!post) {
      return null
    }

    return (
      <AnalyticsClick
        category='module-feed_post'
        action='toggle-report'
        label={ post.is_reported ? 'unreport' : 'report' }
      >
        <FeedLinkLoggedOut type='post' postUid={ postUid }>
          {
            (isAuthenticated) => {
              const icon = (
                <SvgIcon src={ ReportSvg } />
              )
              const handleClick = isAuthenticated ? handleSendReport : undefined

              return (
                <ActionMenuItem
                  icon={ icon }
                  onClick={ handleClick }
                  isDisabled={ post.is_reported }
                >
                  { post.is_reported ? 'Reported' : 'Report' }
                </ActionMenuItem>
              )
            }
          }
        </FeedLinkLoggedOut>
      </AnalyticsClick>
    )
  }, [handleSendReport, post, postUid])

  const {
    allowEdit,
    allowDelete,
    allowFollow,
    allowReport,
  } = useMemo(() => ({
    allowEdit: isOwn && !isPostEditing,
    allowDelete: isOwn,
    allowFollow: !isOwn && (post && post.type !== 'sponsored_post'),
    allowReport: !isOwn,
  }), [isOwn, isPostEditing, post])

  return (
    <ActionMenu
      onHide={ () => { /* do nothing */ } }
      className={ styles.postActionsMenu }
    >
      { allowEdit &&
        <AnalyticsClick category='module-feed_post' action='click-edit'>
          <ActionMenuItem
            icon={ <SvgIcon src={ EditSvg } /> }
            onClick={ handleEditPost }
          >
            Edit
          </ActionMenuItem>
        </AnalyticsClick>}
      { allowDelete && (
        <AnalyticsClick category='module-feed_post' action='click-delete'>
          <ActionMenuItem
            icon={ <SvgIcon src={ CrossSvg } /> }
            onClick={ handleDeletePost }
          >
            Delete
          </ActionMenuItem>
        </AnalyticsClick>
      )}
      { allowFollow && (
        <>
          { followActionButton }
        </>
      )}
      { allowReport && (
        <>
          { reportActionButton }
        </>
      )}
    </ActionMenu>
  )
})
